import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table, Card, Button, Typography, Tag, Space, Select, Popconfirm, App as AntApp,
  Modal, Form, DatePicker, Input, Alert,
} from 'antd';
import { PauseCircleOutlined, PlayCircleOutlined, DeleteOutlined, RollbackOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '@/services/api';
import { useSudoGate } from '@/hooks/useSudoGate';

const { Title } = Typography;

interface UnitRow {
  unitIdx: string; enabled: boolean; start: string | null; expired: string | null;
  createdAt: string | null; planIdx: string; planName: string | null;
  vendorIdx: string; vendorName: string; deviceIdx: string | null;
}

export default function AdminLicenseUnitsPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { require: sudoReq, modal: sudoModal, sudoActive } = useSudoGate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [vendorId, setVendorId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [editUnit, setEditUnit] = useState<UnitRow | null>(null);
  const [search, setSearch] = useState('');
  const [rollbackForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const limit = 50;

  const vendors = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: async () => (await apiClient.get<any>('/admin/organizations')) as any,
  });

  const units = useQuery({
    queryKey: ['admin', 'license-units', status, vendorId, page],
    queryFn: async () => {
      const params: any = { limit, offset: (page - 1) * limit };
      if (status) params.status = status;
      if (vendorId) params.vendorId = vendorId;
      return (await apiClient.get<any>('/admin/license-units', params)) as any;
    },
  });

  const suspendMut = useMutation({
    mutationFn: (id: string) => apiClient.post(`/admin/license-units/${id}/suspend`),
    onSuccess: () => { message.success('Suspended'); qc.invalidateQueries({ queryKey: ['admin', 'license-units'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const unsuspendMut = useMutation({
    mutationFn: (id: string) => apiClient.post(`/admin/license-units/${id}/unsuspend`),
    onSuccess: () => { message.success('Unsuspended'); qc.invalidateQueries({ queryKey: ['admin', 'license-units'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/license-units/${id}`),
    onSuccess: () => { message.success('Deleted'); qc.invalidateQueries({ queryKey: ['admin', 'license-units'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const editMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => apiClient.patch(`/admin/license-units/${id}`, body),
    onSuccess: () => { message.success('Unit updated'); setEditUnit(null); qc.invalidateQueries({ queryKey: ['admin', 'license-units'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const rollbackMut = useMutation({
    mutationFn: (body: any) => apiClient.post('/admin/license-units/batch-rollback', body),
    onSuccess: (data: any) => {
      const n = (data as any)?.rollback?.unitsSuspended ?? 0;
      message.success(`Rollback done: ${n} units suspended`);
      setRollbackOpen(false);
      rollbackForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'license-units'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const unitStatus = (r: UnitRow): 'active' | 'suspended' | 'expired' => {
    if (!r.enabled) return 'suspended';
    if (r.expired && new Date(r.expired) < new Date()) return 'expired';
    return 'active';
  };

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>{t('admin.licenseUnits')}</Title>
        <Space>
          {sudoActive && <Tag color="orange">SUDO</Tag>}
          <Button icon={<RollbackOutlined />} danger onClick={() => sudoReq(() => setRollbackOpen(true))}>{t('admin.batchRollback')}</Button>
        </Space>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="Search UUID / device / plan" prefix={<SearchOutlined />} allowClear
            style={{ width: 280 }} value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select placeholder="All vendors" allowClear style={{ width: 200 }} value={vendorId}
            onChange={(v) => { setVendorId(v); setPage(1); }}
            options={vendors.data?.vendors?.map((v: any) => ({ label: v.name, value: v.idx })) ?? []} />
          <Select placeholder="All status" allowClear style={{ width: 140 }} value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Suspended', value: 'suspended' }, { label: 'Expired', value: 'expired' }]} />
        </Space>
      </Card>

      <Card>
        <Table<UnitRow> size="small" rowKey="unitIdx" loading={units.isLoading}
          dataSource={(units.data?.units ?? []).filter((u: UnitRow) => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (u.unitIdx?.toLowerCase().includes(s)) ||
              (u.deviceIdx?.toLowerCase().includes(s)) ||
              (u.planName?.toLowerCase().includes(s)) ||
              (u.vendorName?.toLowerCase().includes(s));
          })}
          pagination={{ current: page, pageSize: limit, total: units.data?.total ?? 0, onChange: setPage }}
          columns={[
            { title: 'Vendor', dataIndex: 'vendorName', key: 'v', width: 130 },
            { title: 'Plan', dataIndex: 'planName', key: 'p', width: 160 },
            { title: 'Unit UUID', dataIndex: 'unitIdx', key: 'u', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code> },
            { title: 'Device', dataIndex: 'deviceIdx', key: 'd', width: 150, render: (v: string | null) => v ? <code style={{ fontSize: 11 }}>{v.slice(0, 13)}…</code> : '—' },
            { title: 'Created', dataIndex: 'createdAt', key: 'c', width: 110, render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '—' },
            { title: 'Expires', dataIndex: 'expired', key: 'e', width: 110, render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '—' },
            { title: 'Status', key: 's', width: 110, render: (_, r) => {
              const s = unitStatus(r);
              return s === 'active' ? <Tag color="green">Active</Tag> : s === 'suspended' ? <Tag color="orange">Suspended</Tag> : <Tag>Expired</Tag>;
            }},
            { title: 'Actions', key: 'a', width: 260, render: (_, r) => {
              const s = unitStatus(r);
              return (
                <Space size="small">
                  <Button size="small" icon={<EditOutlined />} onClick={() => {
                    setEditUnit(r);
                    editForm.setFieldsValue({ start: r.start ? dayjs(r.start) : null, expired: r.expired ? dayjs(r.expired) : null, enabled: r.enabled });
                  }}>{t('admin.edit')}</Button>
                  {s === 'active' && <Popconfirm title={t('admin.suspend') + '?'} onConfirm={() => suspendMut.mutate(r.unitIdx)}><Button size="small" icon={<PauseCircleOutlined />}>{t('admin.suspend')}</Button></Popconfirm>}
                  {s === 'suspended' && <Popconfirm title={t('admin.resume') + '?'} onConfirm={() => unsuspendMut.mutate(r.unitIdx)}><Button size="small" type="primary" icon={<PlayCircleOutlined />}>{t('admin.resume')}</Button></Popconfirm>}
                  <Popconfirm title="DELETE permanently?" onConfirm={() => sudoReq(() => deleteMut.mutate(r.unitIdx))}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                </Space>
              );
            }},
          ]}
        />
      </Card>

      <Modal title="Batch rollback" open={rollbackOpen} onCancel={() => setRollbackOpen(false)}
        onOk={() => rollbackForm.submit()} confirmLoading={rollbackMut.isPending} okText="Rollback" okButtonProps={{ danger: true }}>
        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
          message="This will SUSPEND all units created in the specified time range." />
        <Form form={rollbackForm} layout="vertical" onFinish={(v) => {
          rollbackMut.mutate({
            from: v.range[0].toISOString(),
            to: v.range[1].toISOString(),
            vendorId: v.vendorId || undefined,
            confirm: 'BATCH_ROLLBACK',
          });
        }}>
          <Form.Item name="range" label="Time range" rules={[{ required: true }]}>
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="vendorId" label="Vendor (optional)">
            <Select allowClear placeholder="All vendors"
              options={vendors.data?.vendors?.map((v: any) => ({ label: v.name, value: v.idx })) ?? []} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Edit LicenseUnit" open={!!editUnit} onCancel={() => setEditUnit(null)}
        onOk={() => editForm.submit()} confirmLoading={editMut.isPending} okText="Save">
        <Form form={editForm} layout="vertical" onFinish={(v) => {
          if (!editUnit) return;
          const body: any = {};
          if (v.start) body.start = v.start.toISOString();
          if (v.expired) body.expired = v.expired.toISOString();
          if (typeof v.enabled === 'boolean') body.enabled = v.enabled;
          editMut.mutate({ id: editUnit.unitIdx, body });
        }}>
          <Form.Item name="start" label="Start date"><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="expired" label="Expiry date"><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="enabled" label="Enabled"><Select options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]} /></Form.Item>
        </Form>
      </Modal>

      {sudoModal}
    </>
  );
}
